/**
 * Revealing a setting that sits inside a collapsed container.
 *
 * Accordions in this app render `{expanded && children}` — collapsed, the controls inside them do
 * not exist in the DOM at all, so a deep link to one waits, finds nothing, and honestly reports that
 * the setting is not on the page. That is true but useless: the setting IS there, one chevron away.
 *
 * The fix is two-sided:
 *  - while a `?focus=` is pending, a collapsed accordion still renders its children, hidden, so the
 *    element can be found (see `useSettingFocusPending`);
 *  - the focus hook then walks up from the element it found and asks each container on the way to
 *    open itself.
 *
 * Containers register by element rather than by declaring which settings they hold. Nothing has to
 * be kept in step with the registry, and a container added later works without being told about.
 */

export const REVEAL_ATTRIBUTE = 'data-setting-reveal';

type RevealFn = () => void;

const reveals = new WeakMap<Element, RevealFn>();

/** Called by a collapsible container with its root element and a callback that expands it. */
export const registerReveal = (element: Element | null, expand: RevealFn): void => {
    if (element) reveals.set(element, expand);
};

export const unregisterReveal = (element: Element | null): void => {
    if (element) reveals.delete(element);
};

/**
 * Open every registered container between `element` and the document root.
 *
 * Returns how many were opened, so the caller knows whether to re-measure: expanding changes layout,
 * and the rectangle taken before the expansion would point at the wrong place.
 */
export const revealAncestors = (element: Element): number => {
    let opened = 0;
    let node: Element | null = element.parentElement;
    while (node) {
        if (node.hasAttribute(REVEAL_ATTRIBUTE)) {
            const expand = reveals.get(node);
            if (expand) {
                expand();
                opened++;
            }
        }
        node = node.parentElement;
    }
    return opened;
};

/**
 * The pending `?focus=` id, kept outside React and outside the router.
 *
 * `useSettingFocus`'s `dismiss()` deliberately clears `focus` from the URL with
 * `window.history.replaceState` rather than the router's `history.replace` - going through the
 * router re-rendered the screen underneath and closed whatever panel the highlighted setting was
 * sitting in (see the comment on `dismiss` for the full story). The cost is that a raw
 * `replaceState` does not fire the `history` package's listeners, so `useLocation()` keeps returning
 * the OLD search string until some later, real navigation happens - fine for `SettingFocusHost`,
 * which tears its ring down from local state it owns directly, but not fine for anything elsewhere
 * in the tree that needs to know "is a focus still pending" (`useSettingReveal`'s hidden-render, and
 * `ConditionalSetting`'s ghost state): those would keep believing a dismissed focus was still live.
 *
 * This tiny store is what `useSettingFocus` writes to instead - on every real URL change AND on
 * `dismiss()` - and what every other pending-focus consumer reads from, so all of them agree.
 * Seeded synchronously from the real URL at module load so a cold page load never has to wait for an
 * effect before the first render sees the right value.
 */
let pendingFocusId: string | undefined =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('focus') ?? undefined : undefined;
const focusIdListeners = new Set<() => void>();

export const getPendingFocusId = (): string | undefined => pendingFocusId;

/** Called by `useSettingFocus` when the URL's `focus` changes, and by its `dismiss()`. */
export const setPendingFocusId = (id: string | undefined): void => {
    if (id === pendingFocusId) return;
    pendingFocusId = id;
    focusIdListeners.forEach((listener) => listener());
};

/** Subscribe to changes; returns the unsubscribe function. For `useSettingFocusId`/`useSettingFocusPending`. */
export const subscribePendingFocusId = (listener: () => void): (() => void) => {
    focusIdListeners.add(listener);
    return () => focusIdListeners.delete(listener);
};
